import { produce } from 'immer';
import { Collection, Map as ImMap, type Set as ImSet, isImmutable, List } from 'immutable';
import { DateTime } from 'luxon';
import { createCachedSelector, LruObjectCache } from 're-reselect';
import { createSelector } from 'reselect';

import { getLength, reduce } from 'app-components/utils/core/immutableHelpers';
import { createAppSelector, createSelectorShallowEqual } from 'app-components/utils/core/reselect';
import { registerCachedSelectors } from 'app-components/utils/core/reselect/selectorCacheRegistry';

import type { Account } from '../accounts';
import { getAccountsById, getPaymentSrcsByAccountId, getRefundSrcsByAccountId } from '../accounts/accountsSelectors';
import { isActiveConnectedAccountFromId } from '../accounts/accountsUtils';
import { getExpenseCOAIds, getIncomeCOAIds } from '../categories/categoriesSelectors';
import { chartOfAccountsTypes } from '../chart-of-accounts';
import { getId } from '../core/resourceStoreTypes';
import { getCurrentDateString } from '../date/dateSelectors';
import { invoiceItemsSelectors } from '../invoice-items/invoiceItemsSlice';
import type { GlobalItemType } from '../invoice-items/invoiceItemTypes';
import {
  getResourcesById,
  getTransactionById,
  getTransactionItemGroupsByCustomerId,
  getTransactionItemsById,
  getTransactionsById,
  getTransactionsOfTypes,
  getTxnsByIdInArAccounts,
  getTxnsInArAccountsByBusinessByCustomer,
  TxnTypes,
  txnTypesCash,
} from './transactionsSelectors';
import type * as transactionsTypes from './transactionsTypes';
import {
  type AllocationProps,
  type Credit,
  type InvoiceTransaction,
  type TransactionItem,
  type TransactionRecord,
  TransactionSubType,
} from './transactionsTypes';
import {
  isInvoiceTxn,
  isSplitTxn,
  isTransferTxn,
  isTxnMatchedOrLinked,
  isUnacceptedScheduledTxn,
  sumAllocations,
  sumUnpaidAmounts,
} from './transactionsUtils';

//
// -- Types & Buckets --
//
export const INVOICE_BUCKETS = ['UNPAID', 'DRAFTS-INVOICE', 'PAID'] as const;
export const ESTIMATE_BUCKETS = ['SENT', 'DRAFTS-ESTIMATE', 'ACCEPTED', 'DECLINED'] as const;
export const SALES_RECEIPT_BUCKETS = ['COMPLETED'] as const; // only 1 bucket (and it will be hidden)
export const INVOICE_TXN_BUCKETS = [...INVOICE_BUCKETS, ...ESTIMATE_BUCKETS, ...SALES_RECEIPT_BUCKETS];

export type InvoiceDashSubtype = Extract<`${TransactionSubType}`, 'INVOICE' | 'ESTIMATE' | 'SALES_RECEIPT'>;

export type InvoiceBucket = (typeof INVOICE_BUCKETS)[number];
export type EstimateBucket = (typeof ESTIMATE_BUCKETS)[number];
export type SalesReceiptBucket = (typeof SALES_RECEIPT_BUCKETS)[number];
export type InvoiceTxnBucket = InvoiceBucket | EstimateBucket | SalesReceiptBucket;

//
// -- Sort helpers --
//

/** Compute the last paid date per invoice from payment allocations. */
const computeLastPaidDates = (
  payments: ReadonlyArray<{
    postedOn?: string;
    allocations?: { forEach: (cb: (alloc: { txnId?: string }) => void) => void } | null;
  }>,
): Record<string, string | undefined> => {
  const lastPaidDates: Record<string, string | undefined> = {};
  for (const payment of payments) {
    if (payment.allocations) {
      payment.allocations.forEach((alloc) => {
        if (
          alloc.txnId &&
          payment.postedOn &&
          (!lastPaidDates[alloc.txnId] || (lastPaidDates[alloc.txnId] ?? '') < payment.postedOn)
        ) {
          lastPaidDates[alloc.txnId] = payment.postedOn;
        }
      });
    }
  }
  return lastPaidDates;
};

const ESTIMATE_STATUS_ORDER: Record<string, number> = {
  DRAFT: 0,
  PENDING: 1,
  ACCEPTED: 2,
  DECLINED: 3,
  CONVERTED: 4,
  EXPIRED: 5,
};

export const getEstimateStatusOrder = (estimate: {
  sentOn?: string | null;
  estimateStatus?: string | null;
}): number => {
  if (!estimate.sentOn) return ESTIMATE_STATUS_ORDER.DRAFT;
  return ESTIMATE_STATUS_ORDER[estimate.estimateStatus ?? 'PENDING'] ?? ESTIMATE_STATUS_ORDER.PENDING;
};

/** Sort invoice group data by the rules defined for each bucket. */
export const sortInvoiceDataForBucket = (
  items: InvoiceGroupData['invoiceData'],
  bucket: InvoiceTxnBucket,
  paidOnDates: Record<string, string | undefined>,
): InvoiceGroupData['invoiceData'] => {
  return [...items].sort((a, b) => {
    switch (bucket) {
      case 'UNPAID':
      case 'DRAFTS-INVOICE': {
        const cmp = (b.invoice.dueOn ?? '').localeCompare(a.invoice.dueOn ?? '');
        return cmp !== 0 ? cmp : (b.invoice.createdAt ?? '').localeCompare(a.invoice.createdAt ?? '');
      }
      case 'PAID': {
        const paidA = paidOnDates[getId(a.invoice)] ?? '';
        const paidB = paidOnDates[getId(b.invoice)] ?? '';
        const cmp = paidB.localeCompare(paidA);
        return cmp !== 0 ? cmp : (b.invoice.dueOn ?? '').localeCompare(a.invoice.dueOn ?? '');
      }
      default: {
        // Estimates (SENT, DRAFTS-ESTIMATE, ACCEPTED, DECLINED) and Sales Receipts (COMPLETED)
        const cmp = (b.invoice.postedOn ?? '').localeCompare(a.invoice.postedOn ?? '');
        return cmp !== 0 ? cmp : Number(b.invoice.invoiceNo ?? 0) - Number(a.invoice.invoiceNo ?? 0);
      }
    }
  });
};

export type InvoiceGroupData = {
  bucketId: InvoiceTxnBucket;
  label: string;
  labelLong: string;
  sum: number;
  subtype: InvoiceDashSubtype;
  invoiceData: Array<{ invoice: InvoiceTransaction; paid: number; unpaid: number }>;
  show: boolean;
};

export type InvoiceStatus = 'overdue' | 'partial' | 'sent' | 'paid' | 'draft';

export function defaultBucket(subtype: InvoiceDashSubtype): InvoiceTxnBucket {
  switch (subtype) {
    case 'SALES_RECEIPT':
      return SALES_RECEIPT_BUCKETS[0];
    case 'ESTIMATE':
      return ESTIMATE_BUCKETS[0];
    case 'INVOICE':
    default:
      return INVOICE_BUCKETS[0];
  }
}

export const bucketsBySubtypeMap: Record<InvoiceDashSubtype, Readonly<Array<InvoiceTxnBucket>>> = {
  SALES_RECEIPT: SALES_RECEIPT_BUCKETS,
  ESTIMATE: ESTIMATE_BUCKETS,
  INVOICE: INVOICE_BUCKETS,
};

export const nameBySubtype: Record<InvoiceDashSubtype, string> = {
  SALES_RECEIPT: 'receipts',
  ESTIMATE: 'estimates',
  INVOICE: 'invoices',
};

//
// -- Get all invoice (any subtype) --
//

export const getInvoiceTypeTxns = createSelectorShallowEqual(getResourcesById, (transactions) =>
  transactions
    .valueSeq()
    .filter((txn) => txn.type === 'INVOICE')
    .toArray(),
);

//
// -- Get subtypes --
//

// This returns all invoices (txn type is INVOICE and subtype is INVOICE)
export const getInvoices = createSelectorShallowEqual(getTxnsByIdInArAccounts, (transactions) =>
  transactions
    .valueSeq()
    .filter((txn) => txn.type === 'INVOICE' && txn.subtype === 'INVOICE')
    .toArray(),
);

export const getSalesReceipts = createSelectorShallowEqual(getResourcesById, (transactions) =>
  transactions
    .valueSeq()
    .filter((txn) => txn.type === 'INVOICE' && txn.subtype === 'SALES_RECEIPT')
    .toArray(),
);

export const getEstimatesForCustomerAndProjectId = createSelector(
  [
    (state, _props) => getTransactionsOfTypes(state, TxnTypes.ESTIMATES),
    (_state, props) => props.customerId,
    (_state, props) => props.projectId,
  ],
  (estimates, customerId, projectId) =>
    estimates
      .reduce((acc: TransactionRecord[], estimate) => {
        if (estimate.customerId === customerId && projectCheckTxn(estimate, projectId)) {
          acc.push(estimate);
        }
        return acc;
      }, [])
      .sort((a, b) => {
        const statusCmp = getEstimateStatusOrder(a) - getEstimateStatusOrder(b);
        if (statusCmp !== 0) return statusCmp;
        return (b.postedOn ?? '').localeCompare(a.postedOn ?? '');
      }),
);

export const getReceiptsForCustomerAndProjectId = createSelector(
  [(state, _props) => getSalesReceipts(state), (_state, props) => props.customerId, (_state, props) => props.projectId],
  (receipts, customerId, projectId) =>
    receipts
      .reduce((acc: TransactionRecord[], receipt) => {
        if (receipt.customerId === customerId && projectCheckTxn(receipt, projectId)) {
          acc.push(receipt);
        }
        return acc;
      }, [])
      .sort((a, b) => {
        const cmp = (b.postedOn ?? '').localeCompare(a.postedOn ?? '');
        return cmp !== 0 ? cmp : Number(b.invoiceNo ?? 0) - Number(a.invoiceNo ?? 0);
      }),
);

// This selector is used to get all the credit transactions that are in the accounts receivable accounts. This includes
// payments, credit-memos, & bad-debt.
export const selectAccountsReceivableCredits = createSelectorShallowEqual([getTxnsByIdInArAccounts], (transactions) =>
  transactions
    .valueSeq()
    .filter((txn) => txn.amount < -0.005)
    .toArray(),
);

const selectPaidByInvoiceIdFromPayments = (payments: TransactionRecord[]) =>
  payments?.reduce<Record<string, number>>((acc, payment) => {
    if (!payment.allocations) return acc;
    payment.allocations.forEach((alloc) => {
      const txnId = alloc.txnId;
      if (!txnId) return;
      const allocAmount = Number.isFinite(alloc.amount) ? alloc.amount : 0;
      // Normalize signed payment allocations to paid amount.
      acc[txnId] = (acc[txnId] ?? 0) + Math.abs(allocAmount);
    });
    return acc;
  }, {});

const resolvePaidForInvoice = (invoice: InvoiceTransaction, paidFromPayments: Record<string, number>) => {
  const paidFromInvoice = sumAllocations(invoice);
  if (Math.abs(paidFromInvoice) > 0.005) {
    return paidFromInvoice;
  }
  return paidFromPayments[getId(invoice)] ?? paidFromInvoice;
};

export const isPaymentBackedInvoicePaidInFull = (invoiceAmount: number, paid: number): boolean =>
  invoiceAmount > 0.005 && paid > 0.005 && paid >= invoiceAmount - 0.005;

export const isPaidInvoiceEditingLocked = (
  invoiceStatus: InvoiceStatus,
  subtype: TransactionSubType | null | undefined,
  invoiceAmount: number,
  paidAmount: number,
): boolean =>
  invoiceStatus === 'paid' &&
  subtype === TransactionSubType.Invoice &&
  isPaymentBackedInvoicePaidInFull(invoiceAmount, paidAmount);

//
// -- Sales & Billing --
//

export const selectDashInvoicesFromSubtype = createSelectorShallowEqual(
  [getInvoiceTypeTxns, (_s, tabName: InvoiceDashSubtype) => tabName],
  // Create shallow eq selector has cache so it should remember all these cases between tab swap
  (invoices, tabName) => {
    switch (tabName) {
      case 'SALES_RECEIPT':
        return invoices.filter((txn) => txn.type === 'INVOICE' && txn.subtype === 'SALES_RECEIPT');
      case 'ESTIMATE':
        return invoices.filter((txn) => txn.type === 'INVOICE' && txn.subtype === 'ESTIMATE');
      case 'INVOICE':
      default:
        return invoices.filter((txn) => txn.type === 'INVOICE' && txn.subtype === 'INVOICE');
    }
  },
);

export const selectGroupedInvoices = createSelectorShallowEqual(
  [
    (s, { subtype }: { subtype: InvoiceDashSubtype }) => selectDashInvoicesFromSubtype(s, subtype),
    selectAccountsReceivableCredits,
    (_s, { subtype }: { subtype: InvoiceDashSubtype }) => subtype,
  ],
  // Create shallow eq selector has cache so it should remember all buckets between swap of tabs if txns do not change
  (invoices, payments, subtype) => {
    const paidFromPayments = selectPaidByInvoiceIdFromPayments(payments);

    const initialVal = INVOICE_TXN_BUCKETS.reduce(
      (acc, bucket) => {
        const label = bucket.includes('DRAFTS') ? 'DRAFTS' : bucket;
        const labelLongBucket = bucket.includes('DRAFTS')
          ? 'Draft'
          : `${label.charAt(0).toUpperCase()}${label.slice(1).toLowerCase()}`;
        acc[bucket] = {
          invoiceData: [] as InvoiceGroupData['invoiceData'],
          sum: 0,
          label,
          labelLong: `${labelLongBucket} ${nameBySubtype[subtype]}`,
          bucketId: bucket,
          subtype,
          show: bucketsBySubtypeMap[subtype].includes(bucket),
        };
        return acc;
      },
      {} as Record<InvoiceTxnBucket, InvoiceGroupData>,
    );

    const paidOnDates = computeLastPaidDates(payments);

    const grouped = invoices.reduce((acc: Record<InvoiceTxnBucket, InvoiceGroupData>, invoice: InvoiceTransaction) => {
      const invoiceAmount = Number.isFinite(invoice.amount) ? invoice.amount : 0;
      const paid = subtype === 'INVOICE' ? resolvePaidForInvoice(invoice, paidFromPayments) : 0;
      // An invoice fully paid via allocations (e.g. applied client credit) at creation time is
      // settled even though it was never explicitly sent, so it should not be treated as a draft.
      const isFullyPaidInvoice = subtype === 'INVOICE' && isPaymentBackedInvoicePaidInFull(invoiceAmount, paid);

      // -- Draft status is shared between some types --
      if (!invoice.sentOn && subtype !== 'SALES_RECEIPT' && !isFullyPaidInvoice) {
        const draftBucket = `DRAFTS-${subtype}` as InvoiceTxnBucket;
        acc[draftBucket].invoiceData.push({ invoice, paid: 0, unpaid: invoice.amount || 0 });
        acc[draftBucket].sum += invoice.amount;
        return acc;
      }

      // -- Get remaining statuses based on tabName --
      switch (subtype) {
        case 'SALES_RECEIPT': {
          acc['COMPLETED'].invoiceData.push({ invoice, paid: invoice.amount || 0, unpaid: 0 });
          acc['COMPLETED'].sum += invoice.amount;
          return acc;
        }
        case 'ESTIMATE': {
          const { estimateStatus } = invoice;
          const estimateBucketId =
            estimateStatus === 'ACCEPTED' ? 'ACCEPTED' : estimateStatus === 'DECLINED' ? 'DECLINED' : 'SENT';
          acc[estimateBucketId].invoiceData.push({ invoice, paid: 0, unpaid: invoice.amount || 0 });
          acc[estimateBucketId].sum += invoice.amount;
          return acc;
        }

        case 'INVOICE': {
          const unpaid = invoiceAmount - paid;
          // Note - Partially paid invoices belong in both groups.
          if (unpaid > 0.005) {
            acc['UNPAID'].invoiceData.push({ invoice, paid, unpaid });
            acc['UNPAID'].sum += unpaid;
          }

          if (paid > 0.005 || invoice.amount < 0.005) {
            acc['PAID'].invoiceData.push({ invoice, paid, unpaid });
            acc['PAID'].sum += paid;
          }
          return acc;
        }
      }
    }, initialVal);

    // Sort each bucket's data
    for (const bucket of INVOICE_TXN_BUCKETS) {
      if (grouped[bucket].invoiceData.length > 1) {
        grouped[bucket].invoiceData = sortInvoiceDataForBucket(grouped[bucket].invoiceData, bucket, paidOnDates);
      }
    }

    return grouped;
  },
);

//
// -- Dashboard summary --
//

export type InvoiceDashboardSummary = {
  overdue: number;
  overdueCount: number;
  dueThisWeek: number;
  dueThisWeekCount: number;
  drafts: number;
  draftsCount: number;
  paidLast90Days: number;
};

export const selectInvoiceDashboardSummary = createSelectorShallowEqual(
  [
    (s) => selectDashInvoicesFromSubtype(s, 'INVOICE' as InvoiceDashSubtype),
    selectAccountsReceivableCredits,
    getCurrentDateString,
  ],
  (invoices, payments, today): InvoiceDashboardSummary => {
    const paidFromPayments = selectPaidByInvoiceIdFromPayments(payments);
    const todayDt = DateTime.fromISO(today);
    const sevenDaysFromNow = todayDt.plus({ days: 7 }).toISODate()!;
    const ninetyDaysAgo = todayDt.minus({ days: 90 }).toISODate()!;

    const paidOnDates = computeLastPaidDates(payments);

    let overdue = 0;
    let overdueCount = 0;
    let dueThisWeek = 0;
    let dueThisWeekCount = 0;
    let drafts = 0;
    let draftsCount = 0;
    let paidLast90Days = 0;

    for (const invoice of invoices) {
      const paid = resolvePaidForInvoice(invoice, paidFromPayments);
      const invoiceAmount = Number.isFinite(invoice.amount) ? invoice.amount : 0;
      const isFullyPaidInvoice = isPaymentBackedInvoicePaidInFull(invoiceAmount, paid);

      // Drafts: unsent invoices that have not already been fully settled.
      if (!invoice.sentOn && !isFullyPaidInvoice) {
        drafts += invoice.amount || 0;
        draftsCount += 1;
        continue;
      }

      const unpaid = invoiceAmount - paid;

      // Overdue: past due with unpaid balance
      if (unpaid > 0.005 && invoice.dueOn && invoice.dueOn < today) {
        overdue += unpaid;
        overdueCount += 1;
      }

      // Due this week: due within next 7 days, not overdue
      if (unpaid > 0.005 && invoice.dueOn && invoice.dueOn >= today && invoice.dueOn <= sevenDaysFromNow) {
        dueThisWeek += unpaid;
        dueThisWeekCount += 1;
      }

      // Paid in last 90 days
      if (paid > 0.005) {
        const lastPaidDate = paidOnDates[getId(invoice)];
        if (lastPaidDate && lastPaidDate >= ninetyDaysAgo) {
          paidLast90Days += paid;
        }
      }
    }

    return { overdue, overdueCount, dueThisWeek, dueThisWeekCount, drafts, draftsCount, paidLast90Days };
  },
);

//
// -- Top Products & Services (paid last 90 days) --
//

export type TopProductOrServiceRow = {
  itemId: string;
  name: string;
  itemType: GlobalItemType;
  isArchived: boolean;
  invoiceCount: number;
  totalAmount: number;
  totalUnits: number;
};

// Split-item types that represent a real product/service line on an invoice.
// Excludes tax, subtotal, discount, shipping, tip, blank, etc.
const PRODUCT_SERVICE_SPLIT_TYPES = new Set<string>([
  'NORMAL',
  'INVOICE_BILLABLE_ITEM',
  'INVOICE_BILLABLE_TIME',
  'INVOICE_BILLABLE_EXPENSE',
  'INVOICE_BUNDLE',
]);

export const selectProductsAndServicesStatsLast90Days = createSelectorShallowEqual(
  [
    (s) => selectDashInvoicesFromSubtype(s, 'INVOICE' as InvoiceDashSubtype),
    selectAccountsReceivableCredits,
    getCurrentDateString,
    invoiceItemsSelectors.selectEntities,
  ],
  (invoices, payments, today, itemsById): TopProductOrServiceRow[] => {
    const paidFromPayments = selectPaidByInvoiceIdFromPayments(payments);
    const ninetyDaysAgo = DateTime.fromISO(today).minus({ days: 90 }).toISODate()!;
    const paidOnDates = computeLastPaidDates(payments);

    const stats: Record<string, TopProductOrServiceRow> = {};

    for (const invoice of invoices) {
      if (!invoice.sentOn) continue;
      const paid = resolvePaidForInvoice(invoice, paidFromPayments);
      if (paid <= 0.005) continue;
      const lastPaidDate = paidOnDates[getId(invoice)];
      if (!lastPaidDate || lastPaidDate < ninetyDaysAgo) continue;

      const items = invoice.split?.items;
      if (!items) continue;

      // Aggregate amounts/units per itemId within a single invoice so invoiceCount counts unique invoices.
      const perInvoice = new Map<string, { amount: number; units: number }>();
      items.forEach((line) => {
        if (!line?.itemId) return;
        if (!line.type || !PRODUCT_SERVICE_SPLIT_TYPES.has(line.type)) return;
        const amt = Number.isFinite(line.amount) ? line.amount : 0;
        const units = Number.isFinite(line.units) ? (line.units as number) : 0;
        const prev = perInvoice.get(line.itemId) ?? { amount: 0, units: 0 };
        perInvoice.set(line.itemId, { amount: prev.amount + amt, units: prev.units + units });
      });

      perInvoice.forEach(({ amount, units }, itemId) => {
        const catalog = itemsById[itemId];
        const existing = stats[itemId];
        if (existing) {
          existing.invoiceCount += 1;
          existing.totalAmount += amount;
          existing.totalUnits += units;
        } else {
          stats[itemId] = {
            itemId,
            name: catalog?.name ?? 'Unknown item',
            itemType: (catalog?.itemType as GlobalItemType | undefined) ?? 'UNKNOWN',
            isArchived: Boolean(catalog?.isArchived),
            invoiceCount: 1,
            totalAmount: amount,
            totalUnits: units,
          };
        }
      });
    }

    return Object.values(stats);
  },
);

//
// -- Status related selectors --
//

export type PaidInvoiceEditAdjustment =
  | { kind: 'additionalPayment'; amountIncrease: number }
  | { kind: 'credit'; creditAmount: number };

export const getPaidInvoiceEditAdjustment = (
  previousAmount: number,
  updatedAmount: number,
  paidAmount: number,
): PaidInvoiceEditAdjustment | undefined => {
  const previousTotal = Math.abs(previousAmount);
  const updatedTotal = Math.abs(updatedAmount);
  // This warning applies only to invoices that were already paid in full.
  if (paidAmount < previousTotal || previousTotal === updatedTotal) return undefined;

  if (updatedTotal > previousTotal && updatedTotal > paidAmount) {
    return {
      kind: 'additionalPayment',
      amountIncrease: updatedTotal - previousTotal,
    };
  }

  if (paidAmount > updatedTotal) {
    return { kind: 'credit', creditAmount: paidAmount - updatedTotal };
  }

  return undefined;
};

export const calculateInvoiceStatus = (
  invoice: transactionsTypes.TransactionProps,
  amountPaid: number,
  today: string,
): InvoiceStatus => {
  const isPaidInFull =
    Math.abs(invoice.amount) <= amountPaid ||
    (invoice.subtype === TransactionSubType.Invoice && isPaymentBackedInvoicePaidInFull(invoice.amount, amountPaid));
  const hasPartialPayment = amountPaid > 0 && !isPaidInFull;
  const isDraft = !invoice.sentOn;
  const isOverdue = invoice.dueOn && invoice.dueOn < today;

  if (isDraft && amountPaid === 0) {
    return 'draft';
  }
  if (isPaidInFull) {
    return 'paid';
  }
  if (isOverdue) {
    return 'overdue';
  }
  if (hasPartialPayment) {
    return 'partial';
  }
  return 'sent';
};

export const getInvoiceStatus = (invoice: transactionsTypes.TransactionProps): InvoiceStatus => {
  const today = DateTime.local().toISODate();
  const amountPaid = sumAllocations(invoice);
  return calculateInvoiceStatus(invoice, amountPaid, today);
};

export const calculateStatusOrder = (status: InvoiceStatus) => {
  switch (status) {
    case 'overdue':
      return 1;
    case 'draft':
      return 2;
    case 'sent':
      return 3;
    case 'partial':
      return 4;
    case 'paid':
      return 5;
    // no default
  }
};

export const calculateEstimateStatus = (invoice: InvoiceTransaction) => {
  if (invoice.subtype !== 'ESTIMATE') {
    throw new Error('calculateEstimateStatus called with non-estimate invoice');
  } else {
    const { estimateStatus } = invoice;
    if (invoice.sentOn && estimateStatus === 'PENDING') {
      return 'sent';
    } else {
      return estimateStatus;
    }
  }
};

export const isInvoiceInDraft = (invoice: transactionsTypes.TransactionProps) => {
  const hasAllocations = invoice.allocations && getLength(invoice.allocations) > 0;
  return !invoice.sentOn && !hasAllocations;
};

export const selectPaidAmountForInvoicesTypes = createSelectorShallowEqual(
  [getInvoiceTypeTxns, selectAccountsReceivableCredits],
  (invoices, payments) => {
    const paidFromPayments = selectPaidByInvoiceIdFromPayments(payments);

    return Object.fromEntries(
      invoices.map((invoice) => {
        const invoiceId = getId(invoice);
        return [invoiceId, resolvePaidForInvoice(invoice, paidFromPayments)];
      }),
    );
  },
);

export const selectInvoiceChipStatuses = createAppSelector(
  [getInvoiceTypeTxns, selectPaidAmountForInvoicesTypes, getCurrentDateString],
  (invoicesTypes, paidAmounts, today) =>
    Object.fromEntries(
      invoicesTypes.map((invoice) => [
        getId(invoice),
        calculateInvoiceStatus(invoice, paidAmounts[getId(invoice)], today),
      ]),
    ),
);

export const getInvoiceStatusByInvoiceId = createAppSelector(
  [selectInvoiceChipStatuses, (_state: unknown, id: string) => id],
  (statuses, id) => statuses[id],
);

//
// -- Invoice, Credits, Payments --
//

export const selectInvoicesLastPaidDate = createSelectorShallowEqual(
  [selectAccountsReceivableCredits],
  computeLastPaidDates,
);

export const selectPaidAmountForInvoices = createSelectorShallowEqual([getInvoices], (invoices) =>
  Object.fromEntries(invoices.map((invoice) => [getId(invoice), sumAllocations(invoice)])),
);

export const selectPaidAmountForProjectId = createAppSelector(
  [
    getInvoices,
    selectPaidAmountForInvoices,
    (_: unknown, props: { projectId: string; customerId: string }) => props.projectId,
    (_: unknown, props: { projectId: string; customerId: string }) => props.customerId,
  ],
  (invoices, paidAmounts, projectId, customerId) => {
    return invoices
      .filter(
        (invoice) =>
          invoice.projectId === projectId ||
          (!invoice.projectId && projectId === 'no-project' && invoice.customerId === customerId),
      )
      .reduce((sum, invoice) => sum + paidAmounts[getId(invoice)], 0);
  },
);

export const selectAllAllocationsForId = createSelectorShallowEqual(
  [
    (state, id: string) => getTransactionById(state, id),
    getTransactionsById,
    (state: Parameters<typeof getTransactionById>[0], id: string): TransactionRecord[] | undefined => {
      const invoice = getTransactionById(state, id);
      // The paid amount can use payment-side allocations when the invoice-side
      // total is effectively zero. Keep any invoice-side rows and add those payments.
      return invoice?.subtype === TransactionSubType.Invoice && Math.abs(sumAllocations(invoice)) <= 0.005
        ? selectAccountsReceivableCredits(state)
        : undefined;
    },
    (_state: unknown, id: string) => id,
  ],
  (invoice, txns, paymentFallback, invoiceId): TransactionRecord[] => {
    const allocations = invoice?.allocations;
    const foundTxnSet = new Set<string>();

    const allocated = reduce(allocations ?? [], [] as TransactionRecord[], (acc, alloc) => {
      if (txns?.has(alloc.txnId) && !foundTxnSet.has(alloc.txnId)) {
        foundTxnSet.add(alloc.txnId);
        acc.push(txns?.get(alloc.txnId) as TransactionRecord);
      }
      return acc;
    });

    paymentFallback?.forEach((payment) => {
      const paymentId = getId(payment);
      if (!foundTxnSet.has(paymentId) && payment.allocations?.some((allocation) => allocation.txnId === invoiceId)) {
        foundTxnSet.add(paymentId);
        allocated.push(payment);
      }
    });

    allocated.sort((a, b) => a.postedOn.localeCompare(b.postedOn));
    return allocated;
  },
);

export const getProjectCreditByBusinessId = createSelector(
  [
    selectAccountsReceivableCredits,
    (_: unknown, customerId: string) => customerId,
    (_: unknown, _customerId: string, projectId: string | 'no-project') => projectId,
  ],
  (paymentTransactions, customerId, projectId) => {
    const bizToCredit = paymentTransactions.reduce<Record<string, number>>(
      (acc, payment) => {
        if (
          payment.customerId === customerId &&
          (payment.projectId === projectId || (projectId === 'no-project' && !payment.projectId))
        ) {
          let creditAmount = Math.abs(payment.amount) ?? 0;
          if (payment.allocations) {
            for (const alloc of payment.allocations) {
              creditAmount -= alloc.amount ?? 0;
            }
          }
          acc.total += creditAmount;
          if (payment.businessId) {
            const currAmount = acc[payment.businessId] ?? 0;
            acc[payment.businessId] = currAmount + creditAmount;
          } else {
            // Payment missing biz id. This shouldn't happen.
            acc.other += creditAmount;
          }
        }
        return acc;
      },
      { total: 0, other: 0 },
    );
    return ImMap(bizToCredit);
  },
);

export const isPayment = (txn: TransactionRecord) => txn.amount < -0.005 && isTransferTxn(txn);

export const getInvoiceCreditedAmount = (allocations?: AllocationProps[]) => {
  let amountFromCredit = 0;
  const paymentAllocIsCredit = new Set();

  // Sum allocs of type credit
  allocations?.forEach((alloc) => {
    if (alloc.type === 'CREDIT') {
      amountFromCredit += alloc.amount;
      paymentAllocIsCredit.add(alloc.txnId);
    }
  });

  // Add tips that were allocated from a payment w/ CREDIT alloc. Note a payment won't have both PAYMENT & CREDIT allocs.
  allocations?.forEach((alloc) => {
    if (alloc.type === 'TIP' && paymentAllocIsCredit.has(alloc.txnId)) {
      amountFromCredit += alloc.amount;
    }
  });
  return amountFromCredit;
};

export const getAvailableCreditTxns = createCachedSelector(
  (_: any, props: transactionsTypes.CreditTxnProps) => props.businessId,
  (_: any, props: transactionsTypes.CreditTxnProps) => props.customerId,
  (_: any, props: transactionsTypes.CreditTxnProps) => props.projectId,
  getTxnsInArAccountsByBusinessByCustomer,
  (businessId, customerId, projectId, txnsByBusinessByCustomer) => {
    // Get all transactions for the given business and customer
    let txns: Collection<string, transactionsTypes.TransactionRecord>;
    if (customerId) {
      if (businessId) {
        // Get all credits for business (and no-business) for customer (and no-customer)
        txns = Collection<string, transactionsTypes.TransactionRecord>().concat(
          txnsByBusinessByCustomer.get(businessId)?.get(customerId) ?? Collection(),
          txnsByBusinessByCustomer.get(businessId)?.get('no-customer') ?? Collection(),
          txnsByBusinessByCustomer.get('no-business')?.get(customerId) ?? Collection(),
          txnsByBusinessByCustomer.get('no-business')?.get('no-customer') ?? Collection(),
        ) as Collection<string, transactionsTypes.TransactionRecord>;
      } else {
        // Get all transactions for customer (and no-customer) across all businesses
        txns = txnsByBusinessByCustomer
          .valueSeq()
          .map((txnsByCustomer) =>
            Collection().concat(
              txnsByCustomer.get(customerId) ?? Collection(),
              txnsByCustomer.get('no-customer') ?? Collection(),
            ),
          )
          .flatten(true) as Collection<string, transactionsTypes.TransactionRecord>;
      }
    } else {
      if (businessId) {
        // Get all transactions for business (and no-business) across all customers
        txns = Collection<string, transactionsTypes.TransactionRecord>().concat(
          txnsByBusinessByCustomer.get(businessId)?.valueSeq().flatten(1) ?? Collection(),
          txnsByBusinessByCustomer.get('no-business')?.valueSeq().flatten(1) ?? Collection(),
        ) as Collection<string, transactionsTypes.TransactionRecord>;
      } else {
        // Get all transactions across all businesses and customers
        txns = txnsByBusinessByCustomer.flatten(2) as Collection<string, transactionsTypes.TransactionRecord>;
      }
    }

    // filter for specified project (if any) and only credit txns that are pending
    return txns.filter((txn) => {
      if (projectId) {
        if (projectId === 'no-project' ? txn.projectId : txn.projectId !== projectId) {
          return false;
        }
      }
      return txn.amount < -0.005 && txn.state === 'PENDING';
    });
  },
)({
  keySelector: (_state: any, props: { businessId?: string | null; customerId?: string | null }) =>
    `${props.businessId ?? 'unspecified-business'}${props.customerId ?? 'unspecified-customer'}`,
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
});

// Credit status order: Partial(0) > Available(1)
export const getCreditStatusOrder = (txn: Pick<transactionsTypes.TransactionProps, 'allocations'>): number =>
  getLength(txn.allocations ?? []) > 0 ? 0 : 1;

/** Sorted credit transactions for display: Partial > Available, then postedOn DESC. */
export const getSortedAvailableCreditTxns = createCachedSelector([getAvailableCreditTxns], (txns) =>
  txns
    .valueSeq()
    .sort((a, b) => {
      const statusCmp = getCreditStatusOrder(a) - getCreditStatusOrder(b);
      if (statusCmp !== 0) return statusCmp;
      return (b.postedOn ?? '').localeCompare(a.postedOn ?? '');
    })
    .toArray(),
)({
  keySelector: (_state: any, props: { businessId?: string | null; customerId?: string | null }) =>
    `${props.businessId ?? 'unspecified-business'}${props.customerId ?? 'unspecified-customer'}`,
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
});

export const getAvailableCredits = createSelector(
  [
    (_, props: transactionsTypes.AvailableCreditsProps) => props.action,
    (_, props: transactionsTypes.AvailableCreditsProps) => props.srcTxn,
    (_, props: transactionsTypes.AvailableCreditsProps) => props.businessId,
    (_, props: transactionsTypes.AvailableCreditsProps) => props.customerId,
    getAvailableCreditTxns,
  ],
  (action, srcTxn, businessId, customerId, txns): Credit[] => {
    if (
      (action !== 'ASSIGN_CREDITS' && action !== 'RECORD_PAYMENT' && (!srcTxn || !isPayment(srcTxn))) ||
      !businessId ||
      !customerId
    ) {
      return [];
    }

    return txns
      .valueSeq()
      .map((txn) => {
        const allocations = (
          List.isList(txn.allocations) ? txn.allocations.toArray() : txn.allocations
        ) as AllocationProps[];
        const allocated = allocations?.reduce((sum, allocation) => sum + Number(allocation.amount ?? 0), 0) ?? 0;
        return {
          txn,
          amount: Math.abs(txn.amount) - allocated,
        };
      })
      .filter((result: Credit) => result.amount > 0.005 && result.txn.id !== srcTxn?.id)
      .sort((a, b) => {
        // Sorting FIFO.
        return a.txn.postedOn > b.txn.postedOn ? 1 : -1;
      })
      .toArray();
  },
);

//** Updates invoice allocation field when virtual "credit" field is changed on invoice form. */
export const getUpdatedAllocations = createSelector(
  [
    (_s: unknown, props: transactionsTypes.UpdatedAllocationsProps) => props.creditedAmount,
    (_s: unknown, props: transactionsTypes.UpdatedAllocationsProps) => props.allocations,
    (_s: unknown, props: transactionsTypes.UpdatedAllocationsProps) => props.businessId,
    (_s: unknown, props: transactionsTypes.UpdatedAllocationsProps) => props.customerId,
    (state, props) => getAvailableCredits(state, { ...props, action: 'ASSIGN_CREDITS' }),
    getTxnsByIdInArAccounts,
  ],
  (creditedAmount, allocations, businessId, customerId, sortedCredits, txns): Array<AllocationProps> => {
    if (!allocations || !businessId || !customerId) return [];
    let newAllocations = (isImmutable(allocations) ? allocations.toJS() : allocations.slice()) as AllocationProps[];
    const currentCreditAmount = getInvoiceCreditedAmount(newAllocations);
    // Keeps track of how many credits we have left to assign / unassign
    let creditsAvailable = creditedAmount - currentCreditAmount;

    // Assign credit allocations to the invoice
    if (creditsAvailable > 0) {
      // Sort by FIFO
      for (const credit of sortedCredits) {
        if (!credit.txn.id) continue; // should not happen.
        if (creditsAvailable <= 0) break;
        const alloc = newAllocations.find((alloc) => alloc.txnId === credit.txn.id && alloc.type === 'CREDIT');
        // Add to existing allocation or create a new one
        if (alloc) {
          alloc.amount += Math.min(creditsAvailable, credit.amount);
        } else {
          newAllocations.push({
            txnId: credit.txn.id,
            type: 'CREDIT',
            amount: Math.min(creditsAvailable, credit.amount),
          });
        }
        creditsAvailable -= credit.amount;
      }
      // Unassign credit allocations from invoice.
    } else if (creditsAvailable < 0) {
      // sort by LIFO
      newAllocations.sort((a, b) =>
        (txns.get(a.txnId)?.postedOn ?? '') > (txns.get(b.txnId)?.postedOn ?? '') ? -1 : 1,
      );
      newAllocations = produce(newAllocations, (draft) => {
        for (const alloc of draft) {
          if (creditsAvailable >= 0) break;
          if (alloc.type === 'CREDIT') {
            const amountToTake = Math.min(alloc.amount, Math.abs(creditsAvailable));
            alloc.amount -= amountToTake;
            creditsAvailable += amountToTake;
          }
        }
      });
    }

    // remove allocations that are 0
    return newAllocations.filter((alloc) => alloc.amount !== 0);
  },
);

export const getAvailableCredit = createCachedSelector([getAvailableCreditTxns], (txns) => {
  const availableCredit = txns.valueSeq().reduce((credit, txn) => {
    const allocations = (txn.allocations ?? List()) as List<transactionsTypes.Allocation>;
    const allocated = allocations.reduce((sum, allocation) => sum + Number(allocation.amount ?? 0), 0.0);
    return credit + (Math.abs(txn.amount) - allocated);
  }, 0.0);
  return availableCredit;
})({
  keySelector: (_state: any, props: { businessId?: string | null; customerId?: string | null }) =>
    `${props.businessId ?? 'unspecified-business'}${props.customerId ?? 'unspecified-customer'}`,
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
});

export const isPaymentCompatibleItem = (
  transactionItem: TransactionItem,
  accountsById: ImMap<string, Account>,
  incomeCOAIds: ImSet<string>,
) => {
  const item = transactionItem.item;
  return (
    item &&
    item.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
    transactionItem.transaction.type !== 'INVOICE' &&
    accountsById.has(item.accountId ?? '') &&
    item.amount > 0.005 &&
    item.coa?.type !== 'ACCOUNT' &&
    (item.coa?.type !== 'BALANCE_ADJUSTMENT' ||
      item.coa?.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER) &&
    incomeCOAIds.includes(item.coa?.id ?? '') &&
    item.id
  );
};

export const isRefundCompatibleItem = (
  transactionItem: TransactionItem,
  accountsById: ImMap<string, Account>,
  expenseCoaIds: ImSet<string>,
) => {
  const item = transactionItem.item;
  return (
    item &&
    item?.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
    accountsById.has(item.accountId ?? '') &&
    item.amount < 0.005 &&
    item.coa?.type !== 'ACCOUNT' &&
    (item.coa?.type !== 'BALANCE_ADJUSTMENT' ||
      item.coa?.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER) &&
    !expenseCoaIds.includes(item.coa?.id ?? '') &&
    item.id
  );
};

export const getPaymentCompatibleTxnItemIds = createSelector(
  [getPaymentSrcsByAccountId, getTransactionItemsById, getIncomeCOAIds],
  (accountsById, txnItemsById, incomeCOAIds) => {
    return txnItemsById.reduce((acc: string[], txnItem: TransactionItem) => {
      const item = txnItem.item;
      if (isPaymentCompatibleItem(txnItem, accountsById, incomeCOAIds) && item.id) {
        acc.push(item.id);
      }
      return acc;
    }, []);
  },
);
export const getRefundCompatibleTxnItemIds = createSelector(
  [getRefundSrcsByAccountId, getTransactionItemsById, getExpenseCOAIds],
  (accountsById, txnItemsById, expenseCoaIds) => {
    return txnItemsById.reduce((acc: string[], txnItem: TransactionItem) => {
      const item = txnItem.item;
      if (isRefundCompatibleItem(txnItem, accountsById, expenseCoaIds) && item.id) {
        acc.push(item.id);
      }
      return acc;
    }, []);
  },
);

export const selectCreditAmountsForCustomerOrProject = createSelector([getAvailableCreditTxns], (credits) => {
  return credits.reduce(
    (sum: any, txn: any) => {
      const sumCopy = sum;
      const allocationSum = Math.abs(sumAllocations(txn)) || 0;

      if (allocationSum === 0 && !txn.sentOn && txn.subtype === TransactionSubType.InvoiceCreditMemo) {
        // draft credit memo
        sumCopy.draft += txn.amount;
      } else {
        sumCopy.total += txn.amount + allocationSum;
      }
      return sumCopy;
    },
    { draft: 0, total: 0 },
  );
});

export const selectOpenCreditsPerBusinessAndProject = createSelector([getAvailableCreditTxns], (credits) =>
  credits.reduce((result: any, txn: any) => {
    if (txn.businessId) {
      if (!result[txn.businessId]) {
        result[txn.businessId] = { total: 0, projects: {} };
      }
      const allocationSum = Math.abs(sumAllocations(txn)) || 0;
      const amount = (txn.amount || 0) + allocationSum;
      result[txn.businessId].total += amount;
      if (txn.projectId) {
        result[txn.businessId].projects[txn.projectId] = (result[txn.businessId].projects[txn.projectId] || 0) + amount;
      }
    }
    return result;
  }, {}),
);

export const selectInvoicesForCustomer = createSelectorShallowEqual(
  [getInvoiceTypeTxns, (_: unknown, customerId: string) => customerId],
  (transactions, customerId) => transactions.filter((txn) => isInvoiceTxn(txn) && txn.customerId === customerId),
);

export const selectInvoicesForProject = createSelectorShallowEqual(
  [getInvoiceTypeTxns, (_: unknown, projectId: string) => projectId],
  (transactions, projectId) => transactions.filter((txn) => isInvoiceTxn(txn) && txn.projectId === projectId),
);
interface UnpaidAmounts {
  sent: number;
  draft: number;
}
export const selectUnpaidAmountsForCustomer = createSelector(
  [selectInvoicesForCustomer],
  (invoices): UnpaidAmounts => sumUnpaidAmounts(invoices),
);

export const selectUnpaidAmountsForProject = createSelector(
  [selectInvoicesForProject],
  (invoices): UnpaidAmounts => sumUnpaidAmounts(invoices),
);

export const getInvoicesForPanel = createSelector(
  [
    getTxnsByIdInArAccounts,
    getCurrentDateString,
    selectInvoicesLastPaidDate,
    (_state, props) => props.projectId,
    (_state, props) => props.customerId,
  ],
  (txnsByIdInArAccounts, date, invoicePaidOn, projectId, customerId) => {
    return txnsByIdInArAccounts
      .valueSeq()
      .reduce((result: TransactionRecord[], txn: TransactionRecord) => {
        let projectCheck = true;
        if (projectId) {
          if (projectId !== 'no-project') {
            projectCheck = txn.projectId === projectId;
          } else {
            projectCheck = !txn.projectId;
          }
        }

        if (
          (customerId ? txn.customerId === customerId : true) &&
          projectCheck &&
          txn.type === 'INVOICE' &&
          txn.subtype === 'INVOICE'
        ) {
          result.push(txn);
        }

        return result;
      }, [])
      .sort((a, b) => {
        const statusA = calculateInvoiceStatus(a, sumAllocations(a), date);
        const statusB = calculateInvoiceStatus(b, sumAllocations(b), date);

        if (statusA === statusB) {
          if (statusA === 'paid') {
            return (invoicePaidOn[getId(a)] ?? '') < (invoicePaidOn[getId(b)] ?? '') ? 1 : -1;
          }
          return (a.dueOn ?? '') > (b.dueOn ?? '') ? 1 : -1;
        }
        return calculateStatusOrder(statusA) - calculateStatusOrder(statusB);
      });
  },
);

// add up all income transactions not in A/R
// add up all A/R transfer transactions
// iterate through all invoice line items
// if sales tax, determine percent of allocation
// determine sales tax amount
// subtract sales tax amount from total amount

// note - unallocated income counts in a/r
// note - flip sign based on account (unearned income account is negative for example)

// unearned income - transfers in and out of an unearned income account (subtype unearned income)

export const getTotalSalesTaxAmountPaid = createSelector(
  [
    getTxnsByIdInArAccounts,
    (_state, props) => props.customerId,
    (_state, props) => props.projectId,
    (_state, props) => props.startDate ?? '1970-01-01',
    (state, props) => props.endDate ?? getCurrentDateString(state),
  ],
  (txnsById, customerId, projectId, startDate, endDate) => {
    let salesTaxAmt = 0;

    txnsById?.forEach((txn) => {
      if (
        !isTransferTxn(txn) ||
        getLength(txn.allocations) === 0 ||
        txn.customerId !== customerId ||
        (isInvoiceTxn(txn) && isInvoiceInDraft(txn)) ||
        // TODO: filter by allocation date
        !(txn.postedOn >= startDate && txn.postedOn <= endDate)
      ) {
        return;
      }

      txn.allocations?.forEach((alloc) => {
        const invoice = txnsById.get(alloc.txnId);
        if (!invoice) return;

        // Check if this allocation applies to the specified project
        let allocProjectCheck = true;

        if (projectId) {
          if (projectId === 'no-project') {
            allocProjectCheck = !invoice.projectId;
          } else {
            allocProjectCheck = invoice.projectId === projectId;
          }
        }

        if (!allocProjectCheck) return;

        const salesTaxAmount = invoice?.split?.items?.find((item) => item.type === 'INVOICE_TAX')?.amount ?? 0;
        const salesTaxContributionPercentage = salesTaxAmount / invoice.amount;
        if (salesTaxContributionPercentage > 0) {
          salesTaxAmt += alloc.amount * salesTaxContributionPercentage;
        }
      });
    });

    return salesTaxAmt;
  },
);

const projectCheck = (txn: TransactionItem, projectId?: string) => {
  let projectCheck = true;
  if (projectId) {
    if (projectId !== 'no-project') {
      projectCheck = txn.item.projectId === projectId;
    } else {
      projectCheck = !txn.item.projectId;
    }
  }
  return projectCheck;
};

const projectCheckTxn = (txn: TransactionRecord, projectId?: string) => {
  let projectCheck = true;
  if (projectId) {
    if (projectId !== 'no-project') {
      projectCheck = txn.projectId === projectId;
    } else {
      projectCheck = !txn.projectId;
    }
  }
  return projectCheck;
};

// add up all income transactions not in A/R
// add up all A/R transfer transactions
// iterate through all invoice line items
// if sales tax, determine percent of allocation
// determine sales tax amount
// subtract sales tax amount from total amount

// note - unallocated income counts in a/r
// note - flip sign based on account (unearned income account is negative for example)

// unearned income - transfers in and out of an unearned income account (subtype unearned income)

export const selectPaidAmountForCustomerId = createSelector(
  [
    (state, props) => getTransactionItemGroupsByCustomerId(state).get(props.customerId ?? ''),
    (state, props) => getTotalSalesTaxAmountPaid(state, { customerId: props.customerId, projectId: props.projectId }),
    (state, props) =>
      getTotalSalesTaxAmountPaid(state, {
        customerId: props.customerId,
        projectId: props.projectId,
        startDate: DateTime.fromISO(getCurrentDateString(state)).startOf('year').toISODate(),
      }),
    getAccountsById,
    (_state, props) => props.accountingType,
    (_state, props) => props.projectId,
    getTransactionsById,
  ],
  (txns, salesTax, salesTaxThisYear, accountsById, accountingType = 'cash', projectId, allTransactions) => {
    const incomeTxns = txns?.get('INCOME')?.filter((txn) => projectCheck(txn, projectId));
    const transferTxns = txns?.get('TRANSFER'); // Don't filter by project here - we'll handle it at allocation level
    let postedOnObj = { id: '', postedOn: '1970-01-01', amount: 0 };
    let totalPaid = 0;
    let thisYearPaid = 0;
    const currentYear = new Date().getFullYear().toString();
    if (incomeTxns) {
      incomeTxns.forEach((txn) => {
        const { subtype, postedOn, amount, id, clientId } = txn.item;

        // we don't include invoices in this calculation for cash accounting
        const isCash = accountingType === 'cash' && subtype !== 'INVOICE';
        const isAccrual = accountingType === 'accrual';

        if (
          (isInvoiceTxn(txn.transaction) && isInvoiceInDraft(txn.transaction)) ||
          (isCash && txn.transaction.type === 'INVOICE' && txn.transaction.subtype !== 'SALES_RECEIPT')
        ) {
          return;
        }

        if (isCash || isAccrual) {
          if (postedOn > postedOnObj.postedOn) {
            postedOnObj = {
              id: id ?? clientId ?? '',
              postedOn,
              amount,
            };
          }
          if (postedOn.startsWith(currentYear)) {
            thisYearPaid += amount;
          }
          totalPaid += amount;
        }
      });
    }
    // Cash based accounting includes when the money is paid, not when the work is done
    // If we transfer to unearned income, we need to include that as income bc transfers remove the income category
    // If we transfer to A/R, the same applies.
    if (accountingType === 'cash' && transferTxns) {
      transferTxns.forEach((txn) => {
        const { postedOn, clientId, id, amount } = txn.item;
        const account = accountsById.get(txn.item?.accountId ?? '');
        const destAccount = accountsById.get(txn.item?.coa?.id ?? '');
        const isUnearned = account?.subType === 'UNEARNED_INCOME' && account?.type === 'OTHER_LIABILITY';
        const isAR = account?.type === 'ACCOUNTS_RECEIVABLE' && destAccount?.subType !== 'SALES_TAX';
        const negator = isUnearned || account?.type === 'ACCOUNTS_RECEIVABLE' ? -1 : 1;

        if (isAR || isUnearned) {
          const updatedAmt = amount * negator;

          // For A/R transfers, we need to check if they have allocations that apply to this project
          if (isAR && txn.transaction.allocations) {
            let projectSpecificAmount = 0;
            txn.transaction.allocations.forEach((alloc) => {
              // Get the allocated transaction to check its project
              const allocatedTxn = allTransactions?.get(alloc.txnId);
              if (allocatedTxn) {
                let allocProjectCheck = true;
                if (projectId) {
                  if (projectId === 'no-project') {
                    allocProjectCheck = !allocatedTxn.projectId;
                  } else {
                    allocProjectCheck = allocatedTxn.projectId === projectId;
                  }
                }

                if (allocProjectCheck) {
                  // Calculate the proportion of this transfer that applies to this project
                  const allocationProportion = (alloc.amount ?? 0) / Math.abs(txn.transaction.amount || 1);
                  projectSpecificAmount += updatedAmt * allocationProportion;
                }
              }
            });

            if (projectSpecificAmount !== 0) {
              if (postedOn > postedOnObj.postedOn) {
                postedOnObj = {
                  id: id ?? clientId ?? '',
                  postedOn,
                  amount: projectSpecificAmount,
                };
              }
              if (postedOn.startsWith(currentYear)) {
                thisYearPaid += projectSpecificAmount;
              }
              totalPaid += projectSpecificAmount;
            }
          } else {
            // For non-AR transfers or transfers without allocations, use original logic
            if (postedOn > postedOnObj.postedOn) {
              postedOnObj = {
                id: id ?? clientId ?? '',
                postedOn,
                amount: updatedAmt,
              };
            }
            if (postedOn.startsWith(currentYear)) {
              thisYearPaid += updatedAmt;
            }
            totalPaid += updatedAmt;
          }
        }
      });
    }
    if (accountingType === 'cash') {
      // note - intentionally not including last payment in subtracting sales tax
      totalPaid -= salesTax;
      thisYearPaid -= salesTaxThisYear;
    }
    const lastPayment = postedOnObj.amount;
    return { totalPaid, lastPayment, thisYearPaid };
  },
);

export const paidInvoiceExists = createSelectorShallowEqual(getInvoices, (invoices) =>
  invoices.some((invoice) =>
    Array.isArray(invoice.allocations) ? invoice.allocations?.length : invoice.allocations?.size,
  ),
);

//
// -- Matching --
//

export const allowSalesReceiptMatch = (txn: TransactionRecord | undefined, incomeCoaIds: ImSet<string>): boolean => {
  if (!txn) return false;
  const cashFlow = txn.type === 'CASH_FLOW';
  const posAmount = txn.amount > 0.005;
  const isIncome = incomeCoaIds.has(txn.coa?.id ?? '') || txn.coa?.type === 'UNCATEGORIZED';

  return !!(
    txn.id &&
    !isSplitTxn(txn) &&
    isIncome &&
    cashFlow &&
    posAmount &&
    !isTxnMatchedOrLinked(txn) &&
    !isUnacceptedScheduledTxn(txn)
  );
};

export const getSalesReceiptMatchCandidateIds = createSelector(
  [
    (s) => getTransactionsOfTypes(s, txnTypesCash),
    (s) => getIncomeCOAIds(s),
    (_s, props: { matchAmount?: number }) => props.matchAmount,
  ],
  (txnsById, incomeCoaIds, matchAmount) => {
    return (
      txnsById?.reduce<string[]>((acc, txn) => {
        if (!txn?.id || !allowSalesReceiptMatch(txn, incomeCoaIds)) {
          return acc;
        }
        const isConnected = isActiveConnectedAccountFromId(txn?.accountId);
        if (matchAmount !== undefined) {
          if (isConnected && txn?.amount === matchAmount) {
            acc.push(txn.id);
          }
        } else {
          acc.push(txn.id);
        }
        return acc;
      }, []) ?? []
    );
  },
);

//
// -- Mixpanel --
//

export const getInvoiceMixpanelData = createSelector(
  [getInvoiceTypeTxns, selectInvoiceChipStatuses],
  (invoices, invoicesByStatus) => {
    const total_invoices = invoices.length;
    const { total_unpaid_invoices, total_past_due_invoices, totalValue } = invoices.reduce(
      (acc, invoice) => {
        const status = invoicesByStatus[getId(invoice)];
        if (status === 'sent') {
          acc.total_unpaid_invoices++;
        } else if (status === 'overdue') {
          acc.total_past_due_invoices++;
        }
        acc.totalValue += invoice.amount;
        return acc;
      },
      { total_unpaid_invoices: 0, total_past_due_invoices: 0, totalValue: 0 },
    );
    const total_value_invoices = Math.round(totalValue * 100) / 100;
    return {
      total_invoices,
      total_unpaid_invoices,
      total_past_due_invoices,
      total_value_invoices,
    };
  },
);

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(getAvailableCreditTxns, getSortedAvailableCreditTxns, getAvailableCredit);
